// File Location: src/components/common/UniversalSearch.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, FileText, Calendar, MapPin, User, AlertCircle, Loader } from 'lucide-react';
import { searchApplicationsUniversal } from '../../services/universalSearchService';

const UniversalSearch = ({ userRole = null }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce search
  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setResults([]);
      setMessage('');
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setLoading(true);
      const response = await searchApplicationsUniversal(searchTerm, userRole);
      
      if (response.success) {
        setResults(response.results);
        setMessage(response.message);
        setShowResults(true);
      } else {
        setResults([]);
        setMessage(response.error || 'Search failed');
      }
      setLoading(false);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, userRole]);

  const handleSelectApplication = (e, application) => {
    // Use the applicationId field (like "SAM00001"), not the Firestore document ID
    // The retrieval functions query by the applicationId field
    const appId = application.applicationId;
    
    if (!appId) {
      console.error('No applicationId found in application:', application);
      return;
    }
    
    let url = '';
    // Determine URL based on user role - using the applicationId field value
    if (userRole === 'hospital') {
      url = `/hospital/application/${appId}`;
    } else if (userRole === 'policy') {
      url = `/policy/details/${appId}`;
    } else if (userRole === 'doctor') {
      url = `/doctor/application-review/${appId}`;
    } else if (userRole === 'finance') {
      url = `/dashboard/financial/review/${appId}`;
    } else if (userRole === 'admin') {
      url = `/admin/hospital-details/${appId}`;
    } else {
      // Default route - try to navigate to the application if it exists
      url = `/hospital/application/${appId}`;
    }
    
    // Support Ctrl+Click or Cmd+Click to open in new tab
    if (e && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      const fullUrl = `${window.location.origin}${url}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
      setShowResults(false);
      setSearchTerm('');
      return;
    }
    
    navigate(url);
    setShowResults(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
    setShowResults(false);
    setMessage('');
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'submitted': 'bg-blue-100 text-blue-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'reviewing': 'bg-purple-100 text-purple-800',
      'disputed': 'bg-orange-100 text-orange-800'
    };

    const statusClass = statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass}`}>
        {status || 'Unknown'}
      </span>
    );
  };

  return (
    <div className="relative w-full" ref={searchRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by Application ID, Patient Name, Case ID..."
          className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 text-sm"
        />
        {searchTerm && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
        {loading && (
          <div className="absolute inset-y-0 right-0 pr-10 flex items-center pointer-events-none">
            <Loader className="h-4 w-4 text-teal-500 animate-spin" />
          </div>
        )}
      </div>

      {/* Results Dropdown */}
      {showResults && results.length > 0 && (
        <div 
          className="absolute z-50 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto"
          ref={resultsRef}
        >
          <div className="p-2">
            <div className="text-xs text-gray-500 mb-2 px-2">
              {message}
            </div>
            {results.map((result) => (
              <div
                key={result.id}
                onClick={(e) => handleSelectApplication(e, result)}
                className="p-3 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-teal-600" />
                      <span className="font-semibold text-gray-900">
                        {result.applicationId}
                      </span>
                      {getStatusBadge(result.status)}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      <User className="h-3 w-3" />
                      <span className="font-medium">{result.patientName}</span>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {result.hospitalName}
                      </div>
                      {result.caseId && (
                        <div className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {result.caseId}
                        </div>
                      )}
                      {result.submittedAt && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {result.submittedAt?.toDate ? 
                            new Date(result.submittedAt.toDate()).toLocaleDateString() : 
                            'N/A'}
                        </div>
                      )}
                    </div>

                    {result.diagnosis && result.diagnosis !== 'N/A' && (
                      <div className="mt-1 text-xs text-gray-500">
                        Diagnosis: {result.diagnosis}
                      </div>
                    )}
                  </div>

                  {result.emergency && (
                    <div className="ml-2">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {showResults && results.length === 0 && !loading && (
        <div className="absolute z-50 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 p-4">
          <div className="text-center text-gray-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">{message || 'No results found'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversalSearch;

